import { templatesIndex } from 'api/algolia';
import { TEMPLATES_SEARCH } from 'constants/mixpanel';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';

import {
  DEFAULT_HITS_PER_PAGE,
  E2_TEMPLATE_CATEGORY,
  MAX_NUM_SEARCH_RESULTS,
} from './usePaginatedTemplatesSearch.config';
import { Layout, PageSize, SearchApiResponse, TemplateFilters } from './usePaginatedTemplatesSearch.types';

/**
 * Perform Algolia search for templates
 * For search query, fetch the max number of results (MAX_NUM_SEARCH_RESULTS)
 * Otherwise, use DEFAULT_HITS_PER_PAGE for pagination
 *
 * @param templateFilters Filters to apply to search query - only `isAccessible` filter option is used here
 *  Other filters are applied manually when parsing results (see parseTemplates fn)
 * @returns
 */
export const fetchTemplates = (templateFilters?: TemplateFilters) => (key: string) => {
  const [query, page] = key.split('_');

  // If query is specified (i.e. user is searching), fetch up to max number of search results
  // Similar to E1 implementation - required as a workaround for template filtering
  const hitsPerPage = !query ? DEFAULT_HITS_PER_PAGE : MAX_NUM_SEARCH_RESULTS;
  let filters = `e2TemplateEnabled:true`;

  // Apply accessibility + plan type filters if specified
  if (templateFilters) {
    if (!!templateFilters.isAccessible) {
      filters += ` AND categories:${E2_TEMPLATE_CATEGORY}`;
    }

    if (!!templateFilters.planType && templateFilters.planType.length > 0) {
      const planTypeFilters = templateFilters.planType.map((planType) => `type:${planType}`);
      filters += ` AND (${planTypeFilters.join(' OR ')})`;
    }
  }

  return templatesIndex.search(query, { hitsPerPage, filters, page: +page }).then((content) => {
    // Trigger MP event after a successful search request to obtain
    // the number of results returned
    if (query && +page === 0) {
      Mixpanel.track(TEMPLATES_SEARCH.eventName, {
        query,
        'number of results': content.nbHits,
      });
    }

    return content;
  });
};

/**
 * Returns true if any filters are applied
 *
 * @param filters
 * @returns boolean
 */
export const checkHasFiltersApplied = (filters: TemplateFilters | null) => {
  if (!filters) return false;
  if (filters.planType && filters.planType.length > 0) return true;
  if (filters.pageSize && filters.pageSize.length > 0) return true;
  if (filters.layout && filters.layout.length > 0) return true;
  if (filters.isAccessible) return true;

  return false;
};

const flattenHits = (d: SearchApiResponse) => d.hits;

/**
 * Parses templates search response from algolia and applies the specified filters if any
 *
 * @param data
 * @param filterOptions
 * @returns
 */
export const parseTemplates = (data: SearchApiResponse[], filterOptions?: TemplateFilters) => {
  if (!data) return [];

  const { pageSize, layout } = filterOptions || {};
  const hasPageSizeFilter = Array.isArray(pageSize) && pageSize.length > 0;
  const hasLayoutFilter = Array.isArray(layout) && layout.length > 0;

  return data
    .filter(Boolean)
    .flatMap(flattenHits)
    .filter((templateHit) => {
      // If no filters are applied, return all templates
      if (!filterOptions) return true;

      // If page size or layout filters aren't applied, return all templates
      // The other filters are applied directly by algolia
      if (!hasPageSizeFilter && !hasLayoutFilter) return true;

      const { width, height } = templateHit;

      let isPageSizeMatch = !hasPageSizeFilter;
      let isLayoutMatch = !hasLayoutFilter;

      // Check page size filter
      if (hasPageSizeFilter && width && height) {
        const isLetter = isLetterSize(width, height);
        if (pageSize.includes(PageSize.Letter) && isLetter) {
          isPageSizeMatch = true;
        } else if (pageSize.includes(PageSize.Square) && width === height) {
          isPageSizeMatch = true;
        } else if (pageSize.includes(PageSize.Custom) && !isLetter) {
          // Custom size is any size that is not letter (same as templates page)
          isPageSizeMatch = true;
        }
      }

      // Check layout filter
      if (hasLayoutFilter) {
        if (layout.includes(Layout.Portrait) && height > width) {
          isLayoutMatch = true;
        } else if (layout.includes(Layout.Landscape) && width > height) {
          isLayoutMatch = true;
        }
      }

      return isPageSizeMatch && isLayoutMatch;
    });
};

/**
 * Check if a template is letter size
 *
 * @param width
 * @param height
 * @returns boolean
 */
const LETTER_SIZE = { width: 816, height: 1056 };
export const isLetterSize = (width: number, height: number) =>
  (width === LETTER_SIZE.width && height === LETTER_SIZE.height) ||
  (height === LETTER_SIZE.width && width === LETTER_SIZE.height);
